import { useEffect, useMemo, useRef, useState } from 'react';
import katex from 'katex';
import 'katex/contrib/mhchem';

interface Props {
  open: boolean;
  initial: string;
  /** Title hint — differs for "insert" vs "edit". */
  title: string;
  /** Chemistry mode shows the compound presets + element builder, aimed at staff who don't know LaTeX. */
  chemistry?: boolean;
  onCancel: () => void;
  onConfirm: (latex: string) => void;
}

// One click inserts a complete, correctly-formed \ce{} formula -- the label uses
// unicode subscripts so staff recognize the compound without reading LaTeX.
const CHEM_PRESETS: Array<[string, string]> = [
  ['H2O', 'H₂O'],
  ['CO2', 'CO₂'],
  ['O2', 'O₂'],
  ['H2SO4', 'H₂SO₄'],
  ['NaCl', 'NaCl'],
  ['HCl', 'HCl'],
  ['CH4', 'CH₄'],
];

const CHEM_ELEMENTS = ['H', 'C', 'O', 'N', 'S', 'Cl'];
const CHEM_NUMBERS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

const CHEM_EXAMPLES: Array<[string, string]> = [
  ['H2O', 'water formula'],
  ['CO2', 'carbon dioxide'],
  ['2H2 + O2 -> 2H2O', 'combustion reaction'],
];

/** Appends `token` to the chemistry body inside \ce{...}, creating the wrapper if needed. */
function appendChemToken(current: string, token: string): string {
  const m = /^\\ce\{([\s\S]*)\}$/.exec(current.trim());
  const body = m ? m[1] : current.trim();
  return `\\ce{${body}${token}}`;
}

/** Modal for entering/editing a LaTeX (or \ce{...} chemistry) formula with a live KaTeX preview. */
export default function MathDialog({ open, initial, title, chemistry, onCancel, onConfirm }: Props) {
  const [latex, setLatex] = useState(initial);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setLatex(initial);
      // Focus after paint so the modal is mounted.
      setTimeout(() => taRef.current?.focus(), 0);
    }
  }, [open, initial]);

  const preview = useMemo(() => {
    if (!latex.trim()) return { html: '', error: false };
    try {
      return { html: katex.renderToString(latex, { displayMode: true, throwOnError: true }), error: false };
    } catch (e) {
      return { html: e instanceof Error ? e.message : 'Invalid formula', error: true };
    }
  }, [latex]);

  if (!open) return null;

  const confirm = () => {
    if (latex.trim()) onConfirm(latex.trim());
  };

  const insertToken = (token: string) => {
    setLatex((cur) => appendChemToken(cur, token));
    taRef.current?.focus();
  };

  return (
    <div className="rte-modal-backdrop" onMouseDown={onCancel}>
      <div className="rte-modal" onMouseDown={(e) => e.stopPropagation()}>
        <h3>{title}</h3>

        {chemistry && (
          <>
            <div className="rte-modal-section-label">Common formulas</div>
            <div className="rte-chem-row">
              {CHEM_PRESETS.map(([formula, label]) => (
                <button
                  key={formula}
                  type="button"
                  className="rte-chem-btn"
                  title={`Insert ${formula}`}
                  onClick={() => setLatex(`\\ce{${formula}}`)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="rte-modal-section-label">Build your own — click to add</div>
            <div className="rte-chem-row">
              {CHEM_ELEMENTS.map((el) => (
                <button key={el} type="button" className="rte-chem-btn" onClick={() => insertToken(el)}>
                  {el}
                </button>
              ))}
              <button type="button" className="rte-chem-btn rte-chem-op" title="Plus" onClick={() => insertToken(' + ')}>+</button>
              <button type="button" className="rte-chem-btn rte-chem-op" title="Reaction arrow" onClick={() => insertToken(' -> ')}>→</button>
            </div>
            <div className="rte-chem-row">
              {CHEM_NUMBERS.map((n) => (
                <button key={n} type="button" className="rte-chem-btn rte-chem-num" onClick={() => insertToken(n)}>
                  {n}
                </button>
              ))}
            </div>
          </>
        )}

        <textarea
          ref={taRef}
          rows={3}
          value={latex}
          placeholder={chemistry ? 'e.g. H2O  or  2H2 + O2 -> 2H2O' : 'e.g. \\frac{a}{b}'}
          onChange={(e) => setLatex(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); confirm(); }
            if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          }}
        />
        <div className={`rte-modal-preview${preview.error ? ' is-error' : ''}`}>
          {preview.error
            ? preview.html
            : preview.html
            ? <span dangerouslySetInnerHTML={{ __html: preview.html }} />
            : <span className="rte-modal-hint" style={{ margin: 0 }}>Preview appears here</span>}
        </div>

        {chemistry ? (
          <ul className="rte-chem-examples">
            {CHEM_EXAMPLES.map(([formula, desc]) => (
              <li key={formula}><code>{formula}</code> → {desc}</li>
            ))}
          </ul>
        ) : (
          <div className="rte-modal-hint">
            LaTeX math, or chemistry with <code>\ce{'{...}'}</code>. Press Ctrl+Enter to insert.
          </div>
        )}

        <div className="rte-modal-actions">
          <button type="button" onClick={onCancel}>Cancel</button>
          <button type="button" className="primary" disabled={!latex.trim() || preview.error} onClick={confirm}>
            Insert
          </button>
        </div>
      </div>
    </div>
  );
}
